"use client";

import { signIn } from "next-auth/react";
import { useLocale } from "next-intl";
import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Link } from "@/i18n/routing";
import { FaEye, FaEyeSlash, FaCircleNotch, FaCopy } from "react-icons/fa";
import MailChecker from "mailchecker";
import validator from "validator";
import { useTranslations } from "next-intl";
import { FcGoogle } from "react-icons/fc";
import type { BTCPayInvoice } from "@/types/btcpay";
import { useSession } from "next-auth/react";
import useFetcherUser from "../checkout/FtecherUser";
import { GrClose } from "react-icons/gr";
import { IoMdClose } from "react-icons/io";

// ─── Constants ────────────────────────────────────────────────────────────────

const IS_DEV = process.env.NODE_ENV === "development";

const MAIN_ORIGIN = IS_DEV
  ? "http://localhost:3000"
  : "https://padelmasterthegame.com";

// ─── Types ────────────────────────────────────────────────────────────────────

interface AuthPopupProps {
  isOpen: boolean;
  onClose: () => void;
  initialMode?: "login" | "signup";
}

interface SessionUser {
  user?: {
    id?: string;
    email?: string | null;
    full_name?: string | null;
  };
}

// ─── Locale maps ─────────────────────────────────────────────────────────────

const localeOfferPaths: Record<string, string> = {
  en: "/account/offer",
  fr: "/compte/offer",
  ar: "/الحساب/العروض",
  nl: "/account/offers",
  de: "/konto/angebote",
  pt: "/conta/ofertas",
  sv: "/konto/erbjudanden",
  ru: "/аккаунт/предложения",
  it: "/account/offerte",
  es: "/cuenta/ofertas",
};

const localeSubdomains: Record<string, string> = {
  en: "padelmasterthegame.com",
  fr: "fr.padelmasterthegame.com",
  ar: "ar.padelmasterthegame.com",
  it: "it.padelmasterthegame.com",
  ru: "ru.padelmasterthegame.com",
  de: "de.padelmasterthegame.com",
  sv: "sv.padelmasterthegame.com",
  nl: "nl.padelmasterthegame.com",
  es: "es.padelmasterthegame.com",
  pt: "pt.padelmasterthegame.com",
};

// ─── Locale → /account path ───────────────────────────────────────────────────
// DAshboarduser handles ?cryptoAfter=1 — the callback MUST land there.
const localeAccountPaths: Record<string, string> = {
  en: "/account", fr: "/compte", ar: "/الحساب",
  nl: "/account", de: "/konto", pt: "/conta",
  sv: "/konto", ru: "/аккаунт", it: "/account", es: "/cuenta",
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function RedirectToCrypto({
  isOpen,
  onClose,
  initialMode = "login",
}: AuthPopupProps) {
  const t = useTranslations("Login");
  const locale = useLocale();
  const th = useTranslations("Padel-Master-The-Game-Home-login-signin");
  const tr = useTranslations("Padel-Master-The-Game-rest");

  const { data: session } = useSession();
  const { userinfo } = useFetcherUser();

  const [affiliateCode, setAffiliateCode] = useState<string | null>(null);
  const [invoice, setInvoice] = useState<BTCPayInvoice | null>(null);
  const [showInvoicePopup, setShowInvoicePopup] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const userIdRef = useRef<string | null>(null);

  // ─── Load affiliate code on mount ─────────────────────────────────────────
  useEffect(() => {
    const code = localStorage.getItem("affiliate");
    setAffiliateCode(code);
  }, []);

  // ─── Poll payment status once invoice popup is open ───────────────────────
  useEffect(() => {
    if (!showInvoicePopup || !userIdRef.current) return;

    const userId = userIdRef.current;

    intervalRef.current = setInterval(async () => {
      try {
        const res = await fetch(`/api/payment-status/${userId}`);
        const data = await res.json();
        if (data.is_payed) {
          clearInterval(intervalRef.current!);
          const domain = localeSubdomains[locale] ?? "padelmasterthegame.com";
          const path = localeOfferPaths[locale] ?? "/account/offer";
          window.location.href = `https://${domain}${path}`;
        }
      } catch (err) {
        console.error("Polling error:", err);
      }
    }, 3000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [showInvoicePopup, locale]);

  const handleInvoiceClose = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setShowInvoicePopup(false);
  };

  // ─── Core invoice handler ─────────────────────────────────────────────────
  const createInvoice = async (sessionOverride?: SessionUser | null) => {
    const activeSession = sessionOverride ?? session;

    if (!activeSession?.user?.id) {
      alert("User not authenticated");
      return;
    }

    try {
      const body: Record<string, any> = {
        amount: "35.47",
        currency: "EUR",
        itemDesc: "Order Created",
        orderId: `order-${Date.now()}`,
        customerEmail: activeSession?.user?.email || undefined,
        customerName:
          (activeSession?.user as any)?.full_name ||
          userinfo?.full_name ||
          undefined,
      };

      if (activeSession.user.id) body.userId = activeSession.user.id;
      if (affiliateCode) body.affiliate_code = affiliateCode;

      const response = await fetch("/api/invoices", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || "Failed to create invoice");
      }

      const invoiceData: BTCPayInvoice = result.data;
      userIdRef.current = activeSession.user.id;
      setInvoice(invoiceData);
      setShowInvoicePopup(true);
    } catch (error) {
      console.error("Error creating invoice:", error);
      alert(
        `Failed to create payment invoice: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    }
  };

  // ─── Post-credentials helper ──────────────────────────────────────────────
  // forceinvoice=true bypasses the stale userinfo check — after a fresh
  // credentials login we don't wait for useFetcherUser to reload.
  const closeAndMaybeInvoice = async (
    freshSession: SessionUser | null,
    forceInvoice = false
  ) => {
    onClose();
    if (forceInvoice || userinfo?.is_payed === false) {
      await createInvoice(freshSession);
    }
  };

  // ─── Form / UI state ──────────────────────────────────────────────────────
  const [mode, setMode] = useState<"login" | "signup">(initialMode);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [error1, setError1] = useState("");
  const [error2, setError2] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordSuggestion, setPasswordSuggestion] = useState("");
  const [isCopied, setIsCopied] = useState(false);
  const [email, setEmail] = useState("");
  const [emailsend, setEmailsend] = useState(false);
  const [emailsenderror, setEmailsenderror] = useState(false);
  const [enteremail, setEnteremail] = useState(false);

  // ── ESC key (only when invoice popup is not open) ─────────────────────────
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen && !showInvoicePopup) onClose();
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose, showInvoicePopup]);

  // ── Body scroll lock ──────────────────────────────────────────────────────
  useEffect(() => {
    document.body.style.overflow =
      isOpen || showInvoicePopup ? "hidden" : "unset";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen, showInvoicePopup]);

  // ── Reset on mode switch ──────────────────────────────────────────────────
  useEffect(() => {
    setError(null);
    setError1("");
    setError2("");
    setEmailsend(false);
    setEmailsenderror(false);
    setEnteremail(false);
    setPasswordSuggestion("");
    setShowPassword(false);
    setIsCopied(false);
  }, [mode]);

  // ─── Validation helpers ───────────────────────────────────────────────────
  const isValidEmail = async (
    emailValue: string
  ): Promise<{ valid: boolean; message?: string }> => {
    if (!emailValue || emailValue.trim() === "")
      return { valid: false, message: t("Email-is-required") };
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailValue))
      return { valid: false, message: t("Invalid-email-format") };
    if (!validator.isEmail(emailValue))
      return { valid: false, message: t("Invalid-email-format") };
    if (!MailChecker.isValid(emailValue))
      return { valid: false, message: t("Disposable-emails") };
    return { valid: true };
  };

  const validatePassword = (password: string): string | null => {
    if (password.length < 8) return t("8-characters");
    if (!/[A-Z]/.test(password)) return t("uppercase");
    if (!/[a-z]/.test(password)) return t("lowercase");
    if (!/[0-9]/.test(password)) return t("number");
    if (!/[!@#$%^&*]/.test(password)) return t("character");
    return null;
  };

  // ─── Reset password ───────────────────────────────────────────────────────
  const resetPassword = async () => {
    if (!email) {
      setEnteremail(true);
      return;
    }
    try {
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_URL}auth/users/reset_password/`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        }
      );
      if (!res.ok) throw new Error();
      setEmailsend(true);
    } catch {
      setEmailsenderror(true);
    }
  };

  // ─── Google login ─────────────────────────────────────────────────────────
  // KEY FIX: query params (?cryptoAfter=1) get silently dropped by next-intl's
  // domain + pathnames-based middleware when it internally rewrites/redirects
  // based on the localized pathname map. sessionStorage survives the OAuth
  // round-trip regardless of what middleware does to the URL.
  const handleGoogleLogin = () => {
    setIsGoogleLoading(true);
    setError(null);
    sessionStorage.setItem("postAuthIntent", "crypto");
    const domain      = localeSubdomains[locale] ?? "padelmasterthegame.com";
    const accountPath = localeAccountPaths[locale] ?? "/account";
    const callbackUrl = `https://${domain}${accountPath}`;
    signIn("google", { callbackUrl });
  };

  // ─── Password suggestion ──────────────────────────────────────────────────
  const generatePasswordSuggestion = () => {
    const chars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*";
    let pw = "";
    for (let i = 0; i < 12; i++)
      pw += chars.charAt(Math.floor(Math.random() * chars.length));
    setPasswordSuggestion(pw);
  };

  const handleCopyPassword = () => {
    if (!passwordSuggestion) return;
    navigator.clipboard.writeText(passwordSuggestion).then(() => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    });
  };

  // ─── Sign-up handler ──────────────────────────────────────────────────────
  const handleSignUp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setError1("");
    setError2("");

    const formData = new FormData(e.currentTarget);
    const emailVal = formData.get("email") as string;
    const password = formData.get("password") as string;

    const emailValidation = await isValidEmail(emailVal);
    if (!emailValidation.valid) {
      setError1(emailValidation.message || t("Invalid-email"));
      setIsLoading(false);
      return;
    }

    const passwordError = validatePassword(password);
    if (passwordError) {
      setError2(passwordError);
      setIsLoading(false);
      return;
    }

    try {
      await fetch(`${process.env.NEXT_PUBLIC_URL}auth/users/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: emailVal, password }),
      });

      const result = await signIn("credentials", {
        redirect: false,
        email: emailVal,
        password,
      });

      if (result?.error) {
        setError(t("Please-verify-your-email-or-password"));
      } else {
        const { getSession } = await import("next-auth/react");
        const freshSession = await getSession();
        // force invoice open — new signups always go to checkout
        await closeAndMaybeInvoice(freshSession, true);
      }
    } catch (err) {
      setError(t("An-error-occurred-during-sign-in"));
      console.error(t("Sign-in-error:"), err);
    } finally {
      setIsLoading(false);
    }
  };

  // ─── Login handler ────────────────────────────────────────────────────────
  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setError1("");
    setError2("");

    const formData = new FormData(e.currentTarget);
    const emailVal = formData.get("email") as string;
    const password = formData.get("password") as string;

    const emailValidation = await isValidEmail(emailVal);
    if (!emailValidation.valid) {
      setError1(emailValidation.message || t("Invalid-email"));
      setIsLoading(false);
      return;
    }

    try {
      const result = await signIn("credentials", {
        redirect: false,
        email: emailVal,
        password,
      });

      if (result?.error) {
        setError(t("Please-verify-your-email-or-password"));
      } else {
        const { getSession } = await import("next-auth/react");
        const freshSession = await getSession();
        // force invoice open — don't rely on stale userinfo
        await closeAndMaybeInvoice(freshSession, true);
      }
    } catch (err) {
      setError(t("An-error-occurred-during-sign-in"));
      console.error(t("Sign-in-error:"), err);
    } finally {
      setIsLoading(false);
    }
  };

  // ─── Invoice portal ───────────────────────────────────────────────────────
  const invoiceModal =
    showInvoicePopup &&
    invoice &&
    createPortal(
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
        <div
          className="bg-black rounded-lg shadow-xl max-w-5xl h-[80vh] relative"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex justify-between items-center p-4 border-b border-yel">
            <p />
            <button
              type="button"
              aria-label="Close payment dialog"
              className="text-yel hover:text-white cursor-pointer"
              onClick={handleInvoiceClose}
            >
              <GrClose size={24} aria-hidden="true" focusable="false" />
            </button>
          </div>
          <div className="h-[calc(100%-4rem)]">
            <p className="text-white justify-center text-center text-sm font-semibold capitalize mt-4 px-4">
              {tr("m1")}
              <br />
              {tr("m2")}
            </p>
            <iframe
              src={invoice.checkoutLink}
              className="w-full h-full"
              title="BTCPay Invoice"
            />
          </div>
        </div>
      </div>,
      document.body
    );

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <>
      {/* Auth popup */}
      {isOpen && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-70 z-50 transition-opacity duration-300"
            onClick={onClose}
          />
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
            <div className="w-full max-w-md mx-auto">
              <div className="relative rounded-2xl shadow-xl overflow-hidden border border-gray-600 bg-black bg-opacity-90 backdrop-filter backdrop-blur-sm animate-fade-in-up">
                <button
                  type="button"
                  aria-label="Close dialog"
                  onClick={onClose}
                  className="absolute top-2 right-2 flex justify-end items-end text-white hover:text-gray-300 transition-colors z-10"
                >
                  <IoMdClose className="w-6 h-6" aria-hidden="true" focusable="false" />
                </button>

                <div className="p-6 sm:p-8">
                  <form
                    onSubmit={mode === "login" ? handleLogin : handleSignUp}
                    className="space-y-4"
                  >
                    <h1 className="font-extrabold text-2xl text-b uppercase">
                      {th("title")}
                    </h1>

                    <div className="mb-6">
                      <h3 className="text-b text-sm font-semibold uppercase">
                        {mode === "login" ? th("Login") : th("Signup")}
                      </h3>
                      <h2 className="text-gray-100 text-sm mt-1">
                        {mode === "login"
                          ? th("login-text")
                          : th("Signup-text")}
                      </h2>
                    </div>

                    {/* Email */}
                    <input
                      type="email"
                      name="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t("email")}
                      className="w-full px-4 py-2 border text-white bg-slate-800 placeholder:text-gray-300 border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-b"
                    />
                    {error1 && <p className="text-b text-sm">{error1}</p>}

                    {/* Password */}
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        placeholder={t("password")}
                        className="w-full px-4 py-2 border text-white bg-slate-800 placeholder:text-gray-300 border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-b"
                        onFocus={() => {
                          if (mode === "signup") generatePasswordSuggestion();
                        }}
                      />
                      <button
                        type="button"
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        aria-pressed={showPassword}
                        onClick={() => setShowPassword((v) => !v)}
                        className={`absolute top-2 text-gray-500 hover:text-gray-700 flex items-center justify-center w-6 h-6 ${
                          locale === "ar" ? "left-2" : "right-2"
                        }`}
                      >
                        {showPassword ? (
                          <FaEyeSlash
                            className="w-5 h-5"
                            aria-hidden="true"
                            focusable="false"
                          />
                        ) : (
                          <FaEye
                            className="w-5 h-5"
                            aria-hidden="true"
                            focusable="false"
                          />
                        )}
                      </button>
                    </div>

                    {/* Password suggestion (signup only) */}
                    {mode === "signup" && passwordSuggestion && (
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-gray-300 text-sm">
                          {t("text-5")} {th("suggestion")}
                          <br />
                          <span className="font-semibold text-b">
                            {passwordSuggestion}
                            <button
                              type="button"
                              aria-label="Copy password"
                              onClick={handleCopyPassword}
                              className="text-gray-300 hover:text-gray-700 ml-2"
                            >
                              <FaCopy
                                className="w-3 h-3 inline"
                                aria-hidden="true"
                                focusable="false"
                              />
                            </button>
                          </span>
                        </p>
                        {isCopied && (
                          <span className="text-sm text-green-500">
                            {t("Copied")}
                          </span>
                        )}
                      </div>
                    )}

                    {error2 && <p className="text-b text-sm">{error2}</p>}
                    {error && <p className="text-b text-sm">{error}</p>}

                    {/* Forgot password (login only) */}
                    {mode === "login" && (
                      <>
                        <p className="text-white text-sm">
                          {th("forgot")}{" "}
                          <span
                            className="font-semibold underline cursor-pointer"
                            onClick={resetPassword}
                          >
                            {t("text-7")}
                          </span>
                        </p>
                        {emailsend && (
                          <p className="text-b text-sm">{t("emailsend")}</p>
                        )}
                        {emailsenderror && (
                          <p className="text-b text-sm">
                            {t("emailsenderror")}
                          </p>
                        )}
                        {enteremail && (
                          <p className="text-b text-sm">{t("enteremail")}</p>
                        )}
                      </>
                    )}

                    {/* Buttons */}
                    <div className="flex flex-col gap-2">
                      {/* Submit */}
                      <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full bg-b text-a py-3 rounded-lg text-sm hover:bg-a hover:text-white font-semibold transition-colors capitalize flex gap-3 justify-center items-center disabled:opacity-70"
                      >
                        {isLoading ? (
                          <>
                            {mode === "login" ? th("Login") : th("Start-now")}…
                            <FaCircleNotch
                              className="animate-spin w-5 h-5"
                              aria-hidden="true"
                              focusable="false"
                            />
                          </>
                        ) : (
                          <>
                            {mode === "login" ? th("Login") : th("Start-now")}
                          </>
                        )}
                      </button>

                      {/* OR divider */}
                      <div className="flex items-center gap-3 my-2">
                        <div className="flex-1 border-t border-gray-500" />
                        <p className="text-gray-400 text-sm font-medium">
                          {th("Or")}
                        </p>
                        <div className="flex-1 border-t border-gray-500" />
                      </div>

                      {/* Google */}
                      <button
                        type="button"
                        onClick={handleGoogleLogin}
                        disabled={isGoogleLoading}
                        className="border border-white w-full rounded-lg py-3 px-12 bg-black hover:bg-gray-800 cursor-pointer flex gap-2 items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {isGoogleLoading ? (
                          <FaCircleNotch
                            className="animate-spin w-5 h-5 text-white"
                            aria-hidden="true"
                            focusable="false"
                          />
                        ) : (
                          <>
                            <FcGoogle size={24} aria-hidden="true" focusable="false" />
                            <p className="text-white capitalize font-semibold text-sm">
                              {th("google")}
                            </p>
                          </>
                        )}
                      </button>
                    </div>

                    {/* Switch mode */}
                    <p className="text-gray-100 text-xs text-center mt-4 capitalize">
                      {mode === "login" ? (
                        <>
                          {th("new")}{" "}
                          <span
                            onClick={() => setMode("signup")}
                            className="text-b font-semibold underline cursor-pointer"
                          >
                            {th("create")}
                          </span>
                        </>
                      ) : (
                        <>
                          {th("have-account")}{" "}
                          <span
                            onClick={() => setMode("login")}
                            className="text-b font-semibold underline cursor-pointer"
                          >
                            {th("Login-here")}
                          </span>
                        </>
                      )}
                    </p>

                    {/* Terms (signup only) */}
                    {mode === "signup" && (
                      <p className="text-gray-400 text-xs text-center mt-2">
                        {th("registering")}{" "}
                        <Link
                          href="/privacy-policy"
                          className="font-semibold underline"
                        >
                          {th("Privacy-Policy")}
                        </Link>{" "}
                        {th("and")}{" "}
                        <Link
                          href="/terms-and-conditions"
                          className="font-semibold underline"
                        >
                          {th("Terms-Conditions")}
                        </Link>
                      </p>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Invoice portal — outside auth popup, no backdrop click */}
      {invoiceModal}

      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in-up {
          animation: fadeInUp 0.3s ease-out;
        }
      `}</style>
    </>
  );
}